// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the end-of-day wrap-up card (design/evening-wrapup r2). The same card sits in the Konvo
// chat (the message Konvo posts 30 minutes before the day ends) and in the corner's pop-up:
//   - offered: agents that can work tonight on the person's own machines (project, machine with a
//     picker of their other machines, ClickUp task), new tasks for them, one button that starts and
//     creates everything ticked, and "Not tonight" (today only)
//   - started: it turns in place into "Tonight is set": one row per agent with its live state, the
//     tasks in one line, Undo for a few minutes, and when the agents digest reports back
//   - skipped or undone: one quiet line
// Rows are ticked and moved through WrapUpStore, so the chat card and the corner stay the same.

import {Routes} from '@app/app/Routes';
import styles from '@app/features/konvo_wrapup/components/WrapUpCard.module.css';
import WrapUpStore from '@app/features/konvo_wrapup/state/WrapUpStore';
import {
	agentStateLine,
	WRAP_UP_COPY as C,
	dayName,
	startLabel,
	tagLabel,
	timeOf,
} from '@app/features/konvo_wrapup/utils/WrapUpCopy';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {
	type WrapUpAgentRow,
	type WrapUpPlan,
	type WrapUpSource,
	type WrapUpTaskRow,
	wrapUpCounts,
} from '@fluxer/schema/src/domains/konvo/WrapUp';
import {useLingui} from '@lingui/react/macro';
import {
	IconCheck,
	IconChecklist,
	IconChevronDown,
	IconChevronRight,
	IconClock,
	IconDeviceLaptop,
	IconFolder,
	IconMoonStars,
	IconPlayerPlay,
	IconPlus,
	IconServer,
	IconSun,
	IconTerminal2,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useEffect, useRef, useState} from 'react';

export type WrapUpCardWhere = 'chat' | 'corner';

function SourceIcon({kind}: {kind: WrapUpSource['kind']}) {
	switch (kind) {
		case 'mail':
			return <OutlookLogo size={14} />;
		case 'whatsapp':
			return <WhatsAppLogo size={14} />;
		case 'konvo':
			return <KonvoLogo size={14} />;
		case 'clickup':
			return <ClickUpLogo size={14} />;
		case 'agents':
			return <IconTerminal2 size={14} stroke={1.75} />;
		case 'promise':
			return <IconChecklist size={14} stroke={1.75} />;
	}
}

function SourceLine({source}: {source: WrapUpSource | null}) {
	if (!source) return null;
	return (
		<div className={styles.from}>
			<SourceIcon kind={source.kind} />
			<span>
				{source.label}
				{source.quote ? ` · “${source.quote}”` : ''}
			</span>
		</div>
	);
}

function Check({on, onToggle, label, disabled}: {on: boolean; onToggle: () => void; label: string; disabled: boolean}) {
	return (
		<button
			type="button"
			role="checkbox"
			aria-checked={on}
			aria-label={label}
			className={clsx(styles.check, on && styles.checkOn)}
			onClick={onToggle}
			disabled={disabled}
			data-flx="konvo-wrapup.row.check"
		>
			{on && <IconCheck size={14} stroke={2.75} />}
		</button>
	);
}

/** The machine chip, with the person's other machines that have the project. */
function MachineChip({plan, row, locked}: {plan: WrapUpPlan; row: WrapUpAgentRow; locked: boolean}) {
	const {i18n} = useLingui();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLSpanElement>(null);
	const machine = plan.machines.find((entry) => entry.id === row.machine_id);
	const options = plan.machines.filter((entry) => entry.projects.includes(row.project_id));
	const canMove = !locked && !row.continue_thread_id && options.length > 1;
	useEffect(() => {
		if (!open) return;
		const close = (event: MouseEvent) => {
			if (!ref.current?.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', close);
		return () => document.removeEventListener('mousedown', close);
	}, [open]);
	const dot = <span className={clsx(styles.dot, machine?.online === false && styles.dotOff)} />;
	if (!canMove)
		return (
			<span className={styles.chip} data-flx="konvo-wrapup.row.machine">
				{dot}
				{row.machine_name}
			</span>
		);
	return (
		<span className={styles.machineWrap} ref={ref}>
			<button
				type="button"
				className={clsx(styles.chip, styles.chipButton, open && styles.chipOpen)}
				aria-haspopup="listbox"
				aria-expanded={open}
				onClick={() => setOpen(!open)}
				data-flx="konvo-wrapup.row.machine"
			>
				{dot}
				{row.machine_name}
				<IconChevronDown size={13} stroke={2} />
			</button>
			{open && (
				<div className={styles.menu} role="listbox" aria-label={i18n._(C.runOn)} data-flx="konvo-wrapup.machine-menu">
					<div className={styles.menuHead}>{i18n._(C.runOn)}</div>
					{options.map((option) => (
						<button
							type="button"
							role="option"
							aria-selected={option.id === row.machine_id}
							key={option.id}
							className={clsx(styles.option, option.id === row.machine_id && styles.optionOn)}
							onClick={() => {
								setOpen(false);
								if (option.id !== row.machine_id)
									void WrapUpStore.edit(plan.id, {rows: [{id: row.id, machine_id: option.id}]});
							}}
							data-flx="konvo-wrapup.machine-option"
						>
							<span className={styles.optionIcon}>
								{option.platform === 'darwin' || option.platform === 'win32' ? (
									<IconDeviceLaptop size={17} stroke={1.75} />
								) : (
									<IconServer size={17} stroke={1.75} />
								)}
								<i className={clsx(styles.optionDot, !option.online && styles.dotOff)} />
							</span>
							<span className={styles.optionText}>
								<span className={styles.optionName}>{option.name}</span>
								<span className={styles.optionSub}>{i18n._(option.online ? C.online : C.offlineStarts)}</span>
							</span>
							{option.id === row.machine_id && <IconCheck size={16} stroke={2.25} />}
						</button>
					))}
				</div>
			)}
		</span>
	);
}

function AgentRow({plan, row, compact}: {plan: WrapUpPlan; row: WrapUpAgentRow; compact: boolean}) {
	const {i18n} = useLingui();
	const locked = plan.status !== 'offered' || WrapUpStore.busy.has(plan.id);
	const task = row.task;
	return (
		<div
			className={clsx(styles.row, styles.agentRow, !row.on && styles.rowOff)}
			data-flx="konvo-wrapup.agent"
			data-row={row.id}
		>
			<Check
				on={row.on}
				label={row.title}
				disabled={locked}
				onToggle={() => void WrapUpStore.edit(plan.id, {rows: [{id: row.id, on: !row.on}]})}
			/>
			<div className={styles.main}>
				<div className={styles.titleLine}>
					<span className={styles.rowTitle}>{row.title}</span>
					{row.tag && <span className={styles.tag}>{tagLabel(i18n, row.tag)}</span>}
					{row.start_at && <span className={styles.when}>{timeOf(row.start_at)}</span>}
				</div>
				{!compact && <SourceLine source={row.source} />}
				{!compact && row.does && <div className={styles.does}>{row.does}</div>}
				<div className={styles.chips}>
					<span className={styles.chip}>
						<IconFolder size={13} stroke={1.75} />
						{row.project_title}
					</span>
					<MachineChip plan={plan} row={row} locked={locked} />
					{task && (
						<span className={styles.chip}>
							<ClickUpLogo size={13} />
							{task.kind === 'existing' ? task.name : i18n._(C.newTask, {list: task.list_name})}
						</span>
					)}
				</div>
			</div>
		</div>
	);
}

function TaskRow({plan, row}: {plan: WrapUpPlan; row: WrapUpTaskRow}) {
	const locked = plan.status !== 'offered' || WrapUpStore.busy.has(plan.id);
	return (
		<div className={clsx(styles.row, !row.on && styles.rowOff)} data-flx="konvo-wrapup.task" data-row={row.id}>
			<Check
				on={row.on}
				label={row.title}
				disabled={locked}
				onToggle={() => void WrapUpStore.edit(plan.id, {rows: [{id: row.id, on: !row.on}]})}
			/>
			<div className={styles.main}>
				<span className={clsx(styles.rowTitle, !row.on && styles.struck)}>{row.title}</span>
				<SourceLine source={row.source} />
			</div>
		</div>
	);
}

function Section({icon, label, count}: {icon: ReactNode; label: string; count: number}) {
	return (
		<div className={styles.section}>
			{icon}
			<span>{label}</span>
			<span className={styles.count}>· {count}</span>
		</div>
	);
}

function OfferedBody({plan, compact, heading}: {plan: WrapUpPlan; compact: boolean; heading: boolean}) {
	const {i18n} = useLingui();
	const counts = wrapUpCounts(plan);
	const busy = WrapUpStore.busy.get(plan.id);
	const day = dayName(plan.next_day, i18n.locale);
	const nothing = counts.agents + counts.tasks === 0;
	return (
		<>
			{heading && (
				<div className={styles.head} data-flx="konvo-wrapup.head">
					<span className={styles.headIcon}>
						<IconMoonStars size={16} stroke={1.9} />
					</span>
					<span className={styles.headText}>
						<span className={styles.headTitle}>{i18n._(C.heading, {day})}</span>
						{plan.holiday && <span className={styles.headSub}>{i18n._(C.holiday, {holiday: plan.holiday})}</span>}
					</span>
				</div>
			)}
			{plan.agents.length > 0 && (
				<>
					<Section
						icon={<IconTerminal2 size={14} stroke={2} />}
						label={i18n._(C.agentsTonight)}
						count={plan.agents.length}
					/>
					{plan.agents.map((row) => (
						<AgentRow key={row.id} plan={plan} row={row} compact={compact} />
					))}
				</>
			)}
			{plan.tasks.length > 0 && (
				<>
					<Section
						icon={<IconPlus size={14} stroke={2} />}
						label={i18n._(C.newTasks, {day})}
						count={plan.tasks.filter((row) => row.on).length}
					/>
					{plan.tasks.map((row) => (
						<TaskRow key={row.id} plan={plan} row={row} />
					))}
				</>
			)}
			{plan.due.length > 0 && (
				<p className={styles.alsoDue} data-flx="konvo-wrapup.due">
					<ClickUpLogo size={13} />
					<span>{i18n._(C.alsoDue, {day, tasks: plan.due.map((task) => task.name).join(', ')})}</span>
				</p>
			)}
			<div className={styles.foot}>
				<button
					type="button"
					className={styles.primary}
					disabled={busy != null || nothing}
					onClick={() => void WrapUpStore.act(plan.id, 'start')}
					data-flx="konvo-wrapup.start"
				>
					<IconPlayerPlay size={16} stroke={2} />
					<span>{startLabel(i18n, counts)}</span>
				</button>
				<button
					type="button"
					className={styles.secondary}
					disabled={busy != null}
					onClick={() => void WrapUpStore.act(plan.id, 'skip')}
					data-flx="konvo-wrapup.skip"
				>
					{i18n._(C.notTonight)}
				</button>
			</div>
		</>
	);
}

function StartedBody({plan, now}: {plan: WrapUpPlan; now: number}) {
	const {i18n} = useLingui();
	const started = plan.agents.filter((row) => row.state !== 'planned');
	const created = [
		...plan.agents.filter((row) => row.created_task).map((row) => row.title),
		...plan.tasks.filter((row) => row.created).map((row) => row.title),
	];
	const machines = [...new Set(started.map((row) => row.machine_name))];
	const canUndo = plan.undo_until != null && Date.parse(plan.undo_until) > now;
	const digest = plan.digest_at ? new Date(plan.digest_at) : null;
	return (
		<div className={styles.status} data-flx="konvo-wrapup.status">
			<div className={styles.statusHead}>
				<span className={styles.ok}>
					<IconCheck size={14} stroke={2.75} />
				</span>
				<span className={styles.statusTitle}>{i18n._(C.tonightSet)}</span>
				{started.length > 0 && (
					<span className={styles.statusSub}>
						{i18n._(C.agentsOn, {count: started.length, machines: machines.join(', ')})}
					</span>
				)}
				<span className={styles.grow} />
				{canUndo && (
					<button
						type="button"
						className={styles.link}
						disabled={WrapUpStore.busy.has(plan.id)}
						onClick={() => void WrapUpStore.act(plan.id, 'undo')}
						data-flx="konvo-wrapup.undo"
					>
						{i18n._(C.undo)}
					</button>
				)}
			</div>
			{started.length > 0 && (
				<div className={styles.statusList}>
					{started.map((row) => {
						const line = agentStateLine(i18n, row, now);
						const live = row.state === 'working';
						const open = row.thread_id
							? () => RouterUtils.transitionTo(Routes.agents(row.machine_id, row.thread_id!))
							: undefined;
						return (
							<button
								type="button"
								key={row.id}
								className={styles.statusRow}
								onClick={open}
								disabled={!open}
								data-flx="konvo-wrapup.status.row"
								data-state={row.state}
							>
								<span className={clsx(styles.stateDot, live && styles.stateLive)}>
									{!live && <IconClock size={13} stroke={2.25} />}
								</span>
								<span className={styles.statusRowTitle}>{row.title}</span>
								<span className={styles.statusWhen}>{line}</span>
								{open && <IconChevronRight size={15} stroke={2} className={styles.chevron} />}
							</button>
						);
					})}
				</div>
			)}
			{created.length > 0 && (
				<p className={styles.statusFoot}>
					<ClickUpLogo size={14} />
					<span>
						<b>{i18n._(C.tasksInClickUp, {count: created.length})}</b> · {created.join(', ')}
					</span>
				</p>
			)}
			{digest && started.length > 0 && (
				<p className={clsx(styles.statusFoot, styles.muted)}>
					<IconSun size={14} stroke={1.75} />
					<span>
						{i18n._(C.digest, {day: dayName(digest.toISOString(), i18n.locale), time: timeOf(digest.toISOString())})}
					</span>
				</p>
			)}
		</div>
	);
}

export const WrapUpCard = observer(function WrapUpCard({
	planId,
	where,
	fallbackTitles,
}: {
	planId: string;
	where: WrapUpCardWhere;
	/** What the message carries, shown while the live plan loads. */
	fallbackTitles?: Array<string>;
}) {
	const {i18n} = useLingui();
	useEffect(() => WrapUpStore.watch(planId), [planId]);
	const plan = WrapUpStore.get(planId);
	const failed = WrapUpStore.failed.get(planId);
	if (!plan) {
		if (WrapUpStore.missing.has(planId)) return <p className={styles.quiet}>{i18n._(C.gone)}</p>;
		return (
			<div className={clsx(styles.card, styles.loading)} aria-busy="true" data-flx="konvo-wrapup.loading">
				{(fallbackTitles ?? []).map((title) => (
					<div key={title} className={styles.loadingRow}>
						{title}
					</div>
				))}
			</div>
		);
	}
	return (
		<div
			className={clsx(styles.card, where === 'corner' && styles.inCorner, plan.status !== 'offered' && styles.settled)}
			data-flx="konvo-wrapup.card"
			data-status={plan.status}
		>
			{plan.status === 'offered' && <OfferedBody plan={plan} compact heading={where === 'chat'} />}
			{plan.status === 'started' && <StartedBody plan={plan} now={WrapUpStore.now} />}
			{plan.status === 'skipped' && (
				<p className={styles.quiet} data-flx="konvo-wrapup.skipped">
					{i18n._(C.skipped)}
				</p>
			)}
			{plan.status === 'undone' && (
				<p className={styles.quiet} data-flx="konvo-wrapup.undone">
					{i18n._(C.undone)}
				</p>
			)}
			{failed && (
				<p className={styles.error} role="alert">
					{i18n._(C.failed)}
				</p>
			)}
		</div>
	);
});
